import { NativeEventListener } from "src/api/NativeEventListener";
import { BehavioralCollector } from "src/collector/BehavioralCollector";
import { DeviceCollector } from "src/collector/DeviceCollector";
import { NetworkCollector } from "src/collector/NetworkCollector";
import { ScreenActivityCollector } from "src/collector/ScreenActivityCollector";
import { SecurityCollector } from "src/collector/SecurityCollector";
import { DataPayload } from "src/di/DataPayload";

import { BaseDataCollector } from "src/core/base/BaseDataCollector";
import { EventCallback } from "src/core/contract/EventCallback";
import { InitializationCallback } from "src/core/contract/InitializationCallback";
import { NetworkService } from "src/core/contract/NetworkService";
import { Event } from "src/core/model/Event";
import { TrackerConfig } from "src/core/model/TrackerConfig";
import { TrackerEventType } from "src/core/model/TrackerEventType";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { PeriodicDataScheduler } from "src/core/scheduler/PeriodicDataScheduler";
import { EventService } from "src/service/EventService";
import { Logger } from "src/util/Logger";

import { EventCoordinator } from "./EventCoordinator";

/**
 * Core engine that coordinates all tracker functionality.
 * Mirrors Android TrackerEngine.
 */
export class TrackerEngine {
  private readonly eventService: EventService;
  private readonly eventCoordinator: EventCoordinator;
  private readonly networkService: NetworkService;
  private readonly payload: DataPayload;
  private readonly scheduler: PeriodicDataScheduler;
  private readonly deviceCollector: DeviceCollector;
  private readonly networkCollector: NetworkCollector;
  private readonly securityCollector: SecurityCollector;
  private readonly behavioralCollector: BehavioralCollector;
  private readonly screenActivityCollector: ScreenActivityCollector;
  private readonly userDefinedValues: Record<string, unknown> = {};

  private isConfigLoaded = false;

  constructor(
    eventService: EventService,
    eventCoordinator: EventCoordinator,
    networkService: NetworkService,
    payload: DataPayload,
    scheduler: PeriodicDataScheduler,
    deviceCollector: DeviceCollector,
    networkCollector: NetworkCollector,
    securityCollector: SecurityCollector,
    behavioralCollector: BehavioralCollector,
    screenActivityCollector: ScreenActivityCollector,
  ) {
    this.eventService = eventService;
    this.eventCoordinator = eventCoordinator;
    this.networkService = networkService;
    this.payload = payload;
    this.scheduler = scheduler;
    this.deviceCollector = deviceCollector;
    this.networkCollector = networkCollector;
    this.securityCollector = securityCollector;
    this.behavioralCollector = behavioralCollector;
    this.screenActivityCollector = screenActivityCollector;

    // Wire user-defined values provider to EventCoordinator
    this.eventCoordinator.setUserDefinedValuesProvider(() => ({ ...this.userDefinedValues }));
  }

  async initialize(callback: InitializationCallback): Promise<void> {
    Logger.debug("Initializing TrackerEngine");

    try {
      const config = await this.networkService.fetchConfiguration();

      if (!config) {
        callback.onComplete(false, "Config fetch returned null");
        return;
      }

      this.payload.setTrackerConfig(config);
      this.isConfigLoaded = true;

      // Initialize collectors that need async setup
      await Promise.all([this.deviceCollector.initFingerprint(), this.networkCollector.fetchIp()]);

      // Initialize security collector with context
      const configuration = this.payload.getConfiguration();
      const deviceId = this.payload.getDeviceId();
      this.securityCollector.init(configuration?.tenant ?? "", deviceId);

      // Initialize behavioral collector
      this.behavioralCollector.init();

      // Start event coordinator (manages all listeners)
      this.eventCoordinator.start();

      // Start event service (queue processor)
      this.eventService.start();

      // Start periodic scheduler
      this.scheduler.start();

      // Start screen activity timer
      this.screenActivityCollector.startTimer();

      Logger.debug("TrackerEngine initialized successfully");
      callback.onComplete(true, "Tracker initialized successfully");
    } catch (error) {
      Logger.error("Failed to initialize:", error);
      const message = error instanceof Error ? error.message : String(error);
      callback.onComplete(false, `Failed to initialize: ${message}`);
    }
  }

  triggerCustomEvent(eventName: string, args: Record<string, unknown>): void {
    if (!this.isConfigLoaded) {
      Logger.error("Config not loaded, cannot trigger custom event");
      return;
    }

    this.eventCoordinator.triggerCustomEvent(eventName, args);
  }

  async triggerCustomEventSync(
    eventName: string,
    args: Record<string, unknown>,
    callback: EventCallback,
  ): Promise<void> {
    if (!this.isConfigLoaded) {
      callback.onError(new Error("Config not loaded"));
      return;
    }

    const mergedArgs = { ...this.userDefinedValues, ...args };
    const config = this.payload.getTrackerConfig();
    if (!config) {
      callback.onError(new Error("Config not available"));
      return;
    }

    const allConfigVariables = this.eventCoordinator.collectAllVariablesNow();
    allConfigVariables.set(TrackerVariableType.CUSTOM_TRIGGER_ARGS, mergedArgs);

    for (const configItem of config.configs) {
      try {
        const trigger = configItem.triggers.find((t) => t.type === TrackerEventType.CUSTOM && t.name === eventName);
        if (!trigger) continue;

        allConfigVariables.set(TrackerVariableType.CURRENT_TRIGGER_NAME, trigger.name);

        // Resolve screen variables
        const screenVars = this.screenActivityCollector.resolveConfigVariables(configItem);
        for (const [k, v] of screenVars) {
          allConfigVariables.set(k, v);
        }

        const variables = BaseDataCollector.mapVariablesToBackend(configItem, allConfigVariables);
        const deviceId = this.payload.getDeviceId();

        const event: Event = {
          name: configItem.event.name,
          key: deviceId,
          variables,
        };

        await this.eventService.sendEventSync(event, callback);
      } catch (error) {
        Logger.error("Error creating event for config:", error);
        callback.onError(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  setUserDefinedValue(key: string, value: unknown): void {
    if (value === null || value === undefined) {
      this.userDefinedValues[key] = undefined;
    } else {
      this.userDefinedValues[key] = value;
    }
  }

  getUserDefinedValues(): Record<string, unknown> {
    return { ...this.userDefinedValues };
  }

  getAllVariables(): Map<TrackerVariableType, unknown> {
    return this.eventCoordinator.collectAllVariablesNow();
  }

  getConfiguration(): TrackerConfig | null {
    return this.payload.getTrackerConfig();
  }

  triggerNativeEvent(eventType: TrackerEventType): void {
    this.eventCoordinator.triggerNativeEvent(eventType);
  }

  setNativeEventListener(listener: NativeEventListener | null): void {
    this.eventCoordinator.setNativeEventListener(listener);
  }

  terminate(): void {
    Logger.debug("Terminating TrackerEngine");
    this.isConfigLoaded = false;

    try {
      this.eventCoordinator.stop();
    } catch (e) {
      Logger.error("Error stopping coordinator:", e);
    }
    try {
      this.eventService.stop();
    } catch (e) {
      Logger.error("Error stopping event service:", e);
    }
    try {
      this.scheduler.stop();
    } catch (e) {
      Logger.error("Error stopping scheduler:", e);
    }
    try {
      this.securityCollector.destroy();
    } catch (e) {
      Logger.error("Error destroying security:", e);
    }
    try {
      this.behavioralCollector.destroy();
    } catch (e) {
      Logger.error("Error destroying behavioral:", e);
    }
    try {
      this.screenActivityCollector.destroy();
    } catch (e) {
      Logger.error("Error destroying screen:", e);
    }

    // Clear user values
    Object.keys(this.userDefinedValues).forEach((key) => {
      this.userDefinedValues[key] = undefined;
    });

    Logger.debug("TrackerEngine terminated");
  }
}
